import {Layout} from '../../src/Layout';
export default Layout;

import docs from 'docs:@react-spectrum/s2';

export const tags = ['list', 'data', 'collection', 'table', 'grid'];
export const relatedPages = [
  {title: 'Testing ListView', url: './ListView/testing'}
];
export const description = 'Displays a list of items with selection and actions.';

# ListView

<PageDescription>{docs.exports.ListView.description}</PageDescription>

```tsx render docs={docs.exports.ListView} links={docs.links} props={['selectionMode', 'selectionStyle', 'isQuiet']} initialProps={{'aria-label': 'Files', selectionMode: 'multiple'}} type="s2" wide
"use client";
import {ListView, ListViewItem, Text} from '@react-spectrum/s2/ListView';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

<ListView
  /* PROPS */
  styles={style({width: 'full', maxWidth: 400, height: 320})}>
  <ListViewItem id="adobe-photoshop" textValue="Adobe Photoshop">
    <Text>Adobe Photoshop</Text>
    <Text slot="description">Image editing software</Text>
  </ListViewItem>
  <ListViewItem id="adobe-xd" textValue="Adobe XD">
    <Text>Adobe XD</Text>
    <Text slot="description">UI/UX design tool</Text>
  </ListViewItem>
  <ListViewItem id="adobe-indesign" textValue="Adobe InDesign">
    <Text>Adobe InDesign</Text>
    <Text slot="description">Desktop publishing</Text>
  </ListViewItem>
</ListView>
```

## Content

`ListView` follows the [Collection Components API](collections.html?component=ListView), accepting both static and dynamic collections. This example shows a dynamic collection, passing a list of objects to the items prop, and a function to render the children.

```tsx render type="s2"
"use client";
import {ListView, ListViewItem} from '@react-spectrum/s2/ListView';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

///- begin collapse -///
let files = [
  {id: 'adobe-photoshop', name: 'Adobe Photoshop'},
  {id: 'adobe-xd', name: 'Adobe XD'},
  {id: 'adobe-indesign', name: 'Adobe InDesign'}
];
///- end collapse -///

<ListView aria-label="Dynamic files" items={files} styles={style({width: 'full', maxWidth: 400, height: 320})}>
  {item => (
    <ListViewItem id={item.id}>{item.name}</ListViewItem>
  )}
</ListView>
```

### Slots

`ListViewItem` supports icons, `Text`, [Image](Image), [ActionMenu](ActionMenu), and [ActionButtonGroup](ActionButtonGroup) as children, and must have a `textValue` prop for accessibility.

```tsx render type="s2"
"use client";
import {ActionButton, Text} from '@react-spectrum/s2/ActionButton';
import {ActionButtonGroup} from '@react-spectrum/s2/ActionButtonGroup';
import {ActionMenu, MenuItem} from '@react-spectrum/s2/ActionMenu';
import {Image} from '@react-spectrum/s2/Image';
import {ListView, ListViewItem} from '@react-spectrum/s2/ListView';
import Edit from '@react-spectrum/s2/icons/Edit';
import Copy from '@react-spectrum/s2/icons/Copy';
import Delete from '@react-spectrum/s2/icons/Delete';
import File from '@react-spectrum/s2/icons/File';
import Share from '@react-spectrum/s2/icons/Share';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

///- begin collapse -///
let documents = [
  {id: 'project-brief', name: 'Project brief.pdf'},
  {id: 'quarterly-report', name: 'Quarterly report.docx'},
  {id: 'budget', name: 'Budget.xlsx'}
];
let images = [
  {id: 'dessert-sunset', name: 'Dessert sunset.jpg', image_url: 'https://images.unsplash.com/photo-1705034598432-1694e203cdf3?q=80&w=600&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'},
  {id: 'hiking-trail', name: 'Hiking trail.png', image_url: 'https://images.unsplash.com/photo-1722233987129-61dc344db8b6?q=80&w=600&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'},
  {id: 'mountain-with-lake', name: 'Mountain with lake.png', image_url: 'https://images.unsplash.com/photo-1722172118908-1a97c312ce8c?q=80&w=600&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'}
];
///- end collapse -///

<div className={style({display: 'flex', gap: 24, width: 'full', flexWrap: 'wrap', justifyContent: 'center'})}>
  <ListView aria-label="Documents" items={documents} styles={style({flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 200, maxWidth: 400, height: 320})}>
    {item => (
      <ListViewItem id={item.id} textValue={item.name}>
        {/*- begin highlight -*/}
        <File />
        <Text>{item.name}</Text>
        <Text slot="description">Document</Text>
        <ActionButtonGroup aria-label="File actions">
          <ActionButton aria-label="Edit">
            <Edit />
          </ActionButton>
          <ActionButton aria-label="Delete">
            <Delete />
          </ActionButton>
        </ActionButtonGroup>
        {/*- end highlight -*/}
      </ListViewItem>
    )}
  </ListView>
  <ListView aria-label="Images" items={images} styles={style({flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 200, maxWidth: 400, height: 320})}>
    {item => (
      <ListViewItem id={item.id} textValue={item.name}>
        {/*- begin highlight -*/}
        <Image src={item.image_url} alt={item.name} />
        <Text>{item.name}</Text>
        <Text slot="description">Image</Text>
        <ActionMenu>
          <MenuItem>
            <Copy />
            <Text>Copy</Text>
          </MenuItem>
          <MenuItem>
            <Share />
            <Text>Share</Text>
          </MenuItem>
          <MenuItem>
            <Delete />
            <Text>Delete</Text>
          </MenuItem>
        </ActionMenu>
        {/*- end highlight -*/}
      </ListViewItem>
    )}
  </ListView>
</div>
```

### Asynchronous loading

Use the `loadingState` and `onLoadMore` props to enable async loading and infinite scrolling.

```tsx render type="s2"
"use client";
import {ListView, ListViewItem} from '@react-spectrum/s2/ListView';
import {useAsyncList} from '@react-spectrum/s2/useAsyncList';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

interface Character {
  name: string
}

function AsyncListView() {
  let list = useAsyncList<Character>({
    async load({signal, cursor}) {
      if (cursor) {
        cursor = cursor.replace(/^http:\/\//i, 'https://');
      }

      let res = await fetch(cursor || 'https://swapi.py4e.com/api/people/?search=', {signal});
      let json = await res.json();
      return {
        items: json.results,
        cursor: json.next
      };
    }
  });

  return (
    <ListView
      aria-label="Star Wars characters"
      ///- begin highlight -///
      loadingState={list.loadingState}
      onLoadMore={list.loadMore}
      ///- end highlight -///
      items={list.items}
      styles={style({width: 'full', maxWidth: 400, height: 320})}>
      {item => (
        <ListViewItem id={item.name}>{item.name}</ListViewItem>
      )}
    </ListView>
  );
}
```

### Links

Use the `href` prop on a `ListViewItem` to create a link. See the [getting started guide](getting-started) to learn how to integrate with your framework. Link interactions vary depending on the selection behavior. See the [selection guide](selection) for more details.

```tsx render docs={docs.exports.ListView} links={docs.links} props={['selectionMode', 'isQuiet', 'hideLinkOutIcon']} initialProps={{'aria-label': 'Bookmarks', selectionMode: 'none'}} type="s2"
"use client";
import {ListView, ListViewItem} from '@react-spectrum/s2/ListView';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

<ListView aria-label="Bookmarks" styles={style({width: 'full', maxWidth: 400, height: 320})}>
  <ListViewItem id="adobe" href="https://adobe.com" target="_blank">
    adobe.com
  </ListViewItem>
  <ListViewItem id="spectrum" href="https://spectrum.adobe.com" target="_blank">
    spectrum.adobe.com
  </ListViewItem>
  <ListViewItem id="react-spectrum" href="https://react-spectrum.adobe.com" target="_blank">
    react-spectrum.adobe.com
  </ListViewItem>
</ListView>
```

### Empty state

Use `renderEmptyState` to render placeholder content when there are no items.

```tsx render type="s2"
"use client";
import {ListView} from '@react-spectrum/s2/ListView';
import {IllustratedMessage, Heading, Content} from '@react-spectrum/s2/IllustratedMessage';
import {Link} from '@react-spectrum/s2/Link';
import FolderOpen from '@react-spectrum/s2/illustrations/linear/FolderOpen';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

<ListView
  aria-label="Search results"
  styles={style({width: 'full', maxWidth: 400})}
  /*- begin highlight -*/
  renderEmptyState={() => (
    <IllustratedMessage>
      <FolderOpen />
      <Heading>No results</Heading>
      <Content>Press <Link href="https://adobe.com">here</Link> for more info.</Content>
    </IllustratedMessage>
  )}>
  {/*- end highlight -*/}
  {[]}
</ListView>
```

## Selection and actions

Use the `selectionMode` prop to enable single or multiple selection. The selected items can be controlled via the `selectedKeys` prop, matching the `id` prop of the items. The `onAction` event handles item actions. Items can be disabled with the `isDisabled` prop. See the [selection guide](selection?component=ListView) for more details.

```tsx render docs={docs.exports.ListView} links={docs.links} props={['selectionMode', 'selectionStyle', 'isQuiet', 'disabledBehavior', 'disallowEmptySelection']} initialProps={{'aria-label': 'Files', selectionMode: 'multiple'}} type="s2"
"use client";
import {ListView, ListViewItem, type Selection} from '@react-spectrum/s2/ListView';
import {ActionBar, ActionButton} from '@react-spectrum/s2/ActionBar';
import Edit from '@react-spectrum/s2/icons/Edit';
import Copy from '@react-spectrum/s2/icons/Copy';
import Delete from '@react-spectrum/s2/icons/Delete';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import {useState} from 'react';

function Example(props) {
  let [selected, setSelected] = useState<Selection>(new Set());

  return (
    <div className={style({width: 'full'})}>
      <ListView
        {...props}
        aria-label="Files"
        styles={style({width: 'full', maxWidth: 400, height: 320})}
        ///- begin highlight -///
        /* PROPS */
        selectedKeys={selected}
        onSelectionChange={setSelected}
        onAction={key => alert(`Action on ${key}`)}
        renderActionBar={(selectedKeys) => {
        ///- end highlight -///
          let selection = selectedKeys === 'all' ? 'all' : [...selectedKeys].join(', ');
          return (
            <ActionBar>
              <ActionButton aria-label="Edit" onPress={() => alert(`Edit ${selection}`)}>
                <Edit />
              </ActionButton>
              <ActionButton aria-label="Copy" onPress={() => alert(`Copy ${selection}`)}>
                <Copy />
              </ActionButton>
              <ActionButton aria-label="Delete" onPress={() => alert(`Delete ${selection}`)}>
                <Delete />
              </ActionButton>
            </ActionBar>
          );
        }}>
        <ListViewItem id="a">Brand guidelines.pdf</ListViewItem>
        <ListViewItem id="b">Icon set.svg</ListViewItem>
        <ListViewItem id="c">Homepage comp.fig</ListViewItem>
        <ListViewItem id="d" isDisabled>Archived.zip</ListViewItem>
      </ListView>
      <p>Current selection: {selected === 'all' ? 'all' : [...selected].join(', ')}</p>
    </div>
  );
}
```

### Navigation

Use the `hasChildItems` prop on `ListViewItem` to display a chevron indicator, signaling that an item can be navigated into. Combine with `onAction` and [Breadcrumbs](Breadcrumbs) to build a drill-down navigation pattern.

```tsx render type="s2"
"use client";
import {Breadcrumbs, Breadcrumb} from '@react-spectrum/s2/Breadcrumbs';
import {ListView, ListViewItem, Text, type Key} from '@react-spectrum/s2/ListView';
import App from '@react-spectrum/s2/icons/App';
import Folder from '@react-spectrum/s2/icons/Folder';
import File from '@react-spectrum/s2/icons/File';
import Image from '@react-spectrum/s2/icons/Image';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import {useState} from 'react';

///- begin collapse -///
type FileItem = {id: string, name: string, icon: React.ReactNode};
type FolderItem = {id: string, name: string, icon: React.ReactNode, children: ListItem[]};
type ListItem = FileItem | FolderItem;
type BreadcrumbItem = {id: string, name: string, icon: React.ReactNode, children: ListItem[]};

let rootItems: ListItem[] = [
  {id: 'photoshop', name: 'Adobe Photoshop', icon: <App />},
  {id: 'xd', name: 'Adobe XD', icon: <App />},
  {id: 'documents', name: 'Documents', icon: <Folder />, children: [
    {id: 'sales-pitch', name: 'Sales Pitch', icon: <File />},
    {id: 'demo', name: 'Demo', icon: <File />},
    {id: 'taxes', name: 'Taxes', icon: <File />}
  ]},
  {id: 'indesign', name: 'Adobe InDesign', icon: <App />},
  {id: 'utilities', name: 'Utilities', icon: <Folder />, children: [
    {id: 'activity-monitor', name: 'Activity Monitor', icon: <App />}
  ]},
  {id: 'aftereffects', name: 'Adobe AfterEffects', icon: <App />},
  {id: 'illustrator', name: 'Adobe Illustrator', icon: <App />},
  {id: 'pictures', name: 'Pictures', icon: <Folder />, children: [
    {id: 'yosemite', name: 'Yosemite', icon: <Image />},
    {id: 'jackson-hole', name: 'Jackson Hole', icon: <Image />},
    {id: 'crater-lake', name: 'Crater Lake', icon: <Image />}
  ]}
];
///- end collapse -///

function NavigationExample() {
  let [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([
    {id: 'root', name: 'Root', icon: <Folder />, children: rootItems}
  ]);

  let currentItems = breadcrumbs[breadcrumbs.length - 1].children;

  let onAction = (key: Key) => {
    let item = currentItems.find(item => item.id === key);
    if (item && 'children' in item) {
      setBreadcrumbs(prev => [...prev, item]);
    }
  };

  let onBreadcrumbAction = (key: Key) => {
    let index = breadcrumbs.findIndex(item => item.id === key);
    setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  };

  return (
    <div className={style({display: 'flex', flexDirection: 'column', gap: 8, width: 'full', maxWidth: 400})}>
      {/*- begin highlight -*/}
      <Breadcrumbs onAction={onBreadcrumbAction}>
        {breadcrumbs.map(item => (
          <Breadcrumb key={item.id} id={item.id}>{item.name}</Breadcrumb>
        ))}
      </Breadcrumbs>
      <ListView
        aria-label={breadcrumbs[breadcrumbs.length - 1].name}
        items={currentItems}
        onAction={onAction}
        styles={style({width: 'full', height: 320})}>
        {item => (
          <ListViewItem id={item.id} textValue={item.name} hasChildItems={item && 'children' in item}>
            {item.icon}
            <Text>{item.name}</Text>
          </ListViewItem>
        )}
      </ListView>
      {/*- end highlight -*/}
    </div>
  );
}
```

## Keyboard navigation

By default, ListView uses arrow key navigation to move focus into rows. Set `keyboardNavigationBehavior="tab"` to have <Keyboard>Tab</Keyboard> move focus in and out of a row.
Use this when rows contain interactive elements such as text fields, where arrow keys and typing in the field should not trigger grid navigation or selection.

```tsx render
"use client";
import {ListView, ListViewItem, Text} from '@react-spectrum/s2/ListView';
import {TextField} from '@react-spectrum/s2/TextField';
import File from '@react-spectrum/s2/icons/File';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};

///- begin collapse -///
let documents = [
  {id: 'project-brief', name: 'Project brief.pdf'},
  {id: 'quarterly-report', name: 'Quarterly report.docx'},
  {id: 'budget', name: 'Budget.xlsx'}
];
///- end collapse -///

<ListView
  /*- begin highlight -*/
  keyboardNavigationBehavior="tab"
  /*- end highlight -*/
  items={documents}
  selectionMode="multiple"
  aria-label="Shared files"
  styles={style({width: 'full', maxWidth: 400, height: 320})}>
  {item => (
    <ListViewItem textValue={item.name}>
      <File />
      <Text>
        <TextField styles={style({margin: 4})} aria-label="title" defaultValue={item.name} placeholder="Enter a name" />
      </Text>
    </ListViewItem>
  )}
</ListView>
```

## Drag and drop

ListView supports drag and drop interactions when the `dragAndDropHooks` prop is provided using the <TypeLink links={docs.links} type={docs.exports.useDragAndDrop} /> hook. Users can drop data on the list as a whole, on individual items, insert new items between existing ones, or reorder items. See the [drag and drop guide](dnd?component=ListView) to learn more.

You can customize the drag preview by passing <TypeLink links={docs.links} type={docs.exports.DragPreview} /> or your own custom drag preview element to `useDragAndDrop`'s `renderDragPreview`. `DragPreview` supports leading icons and `Text` with `label` and `description` slots.

```tsx render type="s2"
"use client";
import {ListView, ListViewItem} from '@react-spectrum/s2/ListView';
import {Text} from '@react-spectrum/s2';
import {style} from '@react-spectrum/s2/style' with {type: 'macro'};
import {DragPreview, useDragAndDrop} from '@react-spectrum/s2/useDragAndDrop';
import {useListData} from '@react-spectrum/s2/useListData';
import File from '@react-spectrum/s2/icons/File';

function ReorderableList() {
  let list = useListData({
    initialItems: [
      {id: '1', name: 'Adobe Photoshop'},
      {id: '2', name: 'Adobe XD'},
      {id: '3', name: 'Adobe InDesign'},
      {id: '4', name: 'Adobe AfterEffects'}
    ]
  });

  ///- begin highlight -///
  let {dragAndDropHooks} = useDragAndDrop({
    getItems: (keys, items: typeof list.items) => items.map(item => ({'text/plain': item.name})),
    onReorder(e) {
      if (e.target.dropPosition === 'before') {
        list.moveBefore(e.target.key, e.keys);
      } else if (e.target.dropPosition === 'after') {
        list.moveAfter(e.target.key, e.keys);
      }
    },
    renderDragPreview: (items) => (
      <DragPreview items={items}>
        <File />
        <Text>{items[0]['text/plain']}</Text>
      </DragPreview>
    )
  });
  ///- end highlight -///

  return (
    <ListView
      aria-label="Reorderable files"
      selectionMode="multiple"
      items={list.items}
      ///- begin highlight -///
      dragAndDropHooks={dragAndDropHooks}
      ///- end highlight -///
      styles={style({width: 'full', maxWidth: 320})}>
      {item => (
        <ListViewItem textValue={item.name}>
          <File />
          <Text>{item.name}</Text>
        </ListViewItem>
      )}
    </ListView>
  );
}
```

## API

```tsx links={{ListView: '#listview', ListViewItem: '#listviewitem', ActionMenu: 'ActionMenu', ActionButtonGroup: 'ActionButtonGroup', Icon: 'icons', Text: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/span', Image: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Element/img'}}
<ListView>
  <ListViewItem>
    <Icon />
    <Image />
    <Text />
    <Text slot="description" />
    <ActionMenu /> or <ActionButtonGroup />
  </ListViewItem>
</ListView>
```

### ListView

<PropTable component={docs.exports.ListView} links={docs.links} />

### ListViewItem

<PropTable component={docs.exports.ListViewItem} links={docs.links} showDescription />
